<template>
    <div class="border-t border-gray-200 dark:border-gray-700 first:border-t-0">
        <div 
            @click="toggleCollapsed"
            class="flex items-center justify-between p-3 cursor-pointer transition-colors hover:bg-gray-50 dark:hover:bg-gray-800"
            :class="isCollapsed ? 'bg-white dark:bg-gray-900' : 'bg-indigo-50 dark:bg-indigo-900/20'"
        >
            <div class="flex items-center space-x-2">
                <component :is="icon" class="h-4 w-4" :class="iconClass" />
                <p class="font-medium text-sm" :class="titleClass">{{ title }}</p>
                <span
                    v-if="periodLabel"
                    class="inline-flex items-center rounded-full bg-gray-100 px-2 py-0.5 text-[11px] font-medium text-gray-500 dark:bg-gray-700 dark:text-gray-400"
                >
                    {{ periodLabel }}
                </span>
                <span v-if="badge" class="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium" :class="badgeClass">
                    {{ badge }}
                </span>
            </div>
            <div class="flex items-center space-x-2">
                <!-- Summary sempre visível quando não está carregando, skeleton apenas quando collapsed e loading -->
                <div v-if="summary || isLoading" class="text-xs text-gray-500 dark:text-gray-400">
                    <div v-if="isLoading && isCollapsed" class="flex space-x-1">
                        <div class="h-3 bg-gray-300 dark:bg-gray-600 rounded animate-pulse w-16"></div>
                        <div class="h-3 bg-gray-300 dark:bg-gray-600 rounded animate-pulse w-20"></div>
                    </div>
                    <span v-else-if="summary">{{ summary }}</span>
                </div>
                <ChevronUpIcon 
                    v-if="!isCollapsed" 
                    class="h-4 w-4 text-gray-400 dark:text-gray-500" 
                />
                <ChevronDownIcon 
                    v-else 
                    class="h-4 w-4 text-gray-400 dark:text-gray-500" 
                />
            </div>
        </div>
        
        <!-- Sempre renderiza o conteúdo, mas controla a visibilidade -->
        <div 
            class="transition-all duration-200 overflow-hidden border-t border-gray-200 dark:border-gray-700"
            :class="isCollapsed ? 'h-0 opacity-0' : 'p-3 bg-white dark:bg-gray-800 opacity-100'"
        >
            <slot />
        </div>
    </div>
</template>

<script>
import { ChevronUpIcon, ChevronDownIcon } from '@heroicons/vue/24/outline'

export default {
    components: {
        ChevronUpIcon,
        ChevronDownIcon
    },
    name: 'CollapsibleInfoCard',
    props: {
        title: {
            type: String,
            required: true
        },
        icon: {
            type: String,
            required: true
        },
        collapsed: {
            type: Boolean,
            default: true
        },
        summary: {
            type: String,
            default: null
        },
        badge: {
            type: String,
            default: null
        },
        periodLabel: {
            type: String,
            default: null
        },
        priority: {
            type: String,
            default: 'normal', // normal, warning, danger
            validator: value => ['normal', 'warning', 'danger'].includes(value)
        },
        loading: {
            type: Boolean,
            default: false
        }
    },
    
    data() {
        return {
            isCollapsed: this.collapsed
        };
    },
    
    computed: {
        iconClass() {
            const classes = {
                normal: 'text-gray-400 dark:text-gray-500',
                warning: 'text-yellow-500 dark:text-yellow-400',
                danger: 'text-red-500 dark:text-red-400'
            };
            return classes[this.priority];
        },
        
        titleClass() {
            const classes = {
                normal: 'text-gray-700 dark:text-gray-300',
                warning: 'text-yellow-700 dark:text-yellow-300',
                danger: 'text-red-700 dark:text-red-300'
            };
            return classes[this.priority];
        },
        
        badgeClass() {
            const classes = {
                normal: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400',
                warning: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400',
                danger: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400'
            };
            return classes[this.priority];
        },

        isLoading() {
            return this.loading || (this.summary && (this.summary.includes('Carregando') || this.summary.includes('carregando')));
        }
    },
    
    methods: {
        toggleCollapsed() {
            this.isCollapsed = !this.isCollapsed;
            this.$emit('toggle', this.isCollapsed);
        }
    },
    
    watch: {
        collapsed(newValue) {
            this.isCollapsed = newValue;
        },
        
        isCollapsed(newValue) {
            this.$emit('update:collapsed', newValue);
        }
    }
};
</script> 